import { Injectable, Renderer2 } from '@angular/core';
import {
  AttributeBase,
  FieldsToBeEdited,
  LegSyncingFields
} from '@approot/models';
import {
  AdminService,
  PricingExtraService,
  ProductsConfigService
} from '@approot/services';
import { Utilities } from '@approot/utilities';
import { clearLastFixingData } from '@approot/utilities/schedule-utilities';
import {
  nonRfqAbleCrosses,
  setupCurrencyWhenCrossChanges
} from '../utilities/cross-utilities';
import {
  faceCurrencyChange,
  setCurrencyOptions
} from '../utilities/currency-utilities';
import { getAllAttributeOptions } from '../utilities/leg-attribute-utilities';
import {
  disableAndWipeAttribute,
  dropDownSideInfoSetter,
  enableAttribute,
  makeTextCustomColor,
  optionsChangeHandler,
  wipeAttributeInfo
} from '../utilities/leg.utilities';
import {
  counterNotionalOrPair,
  notionalShortLongOnCrossChange
} from '../utilities/notional-utilities';
import { LegCurrencyService } from './leg-currency.service';
import { LegDateService } from './leg-date.service';
import { LegHelperService } from './leg-helper.service';
import { LegMiscellaneousService } from './leg-miscellaneous.service';
import { LegNotionalService } from './leg-notional.service';
import { LegScheduleService } from './leg-schedule.service';
import {
  getStrikeValue,
  LegSpotStrikeService
} from './leg-spot-strike.service';

@Injectable({
  providedIn: 'root'
})
export class LegCrossService {
  constructor(
    private legHelperService: LegHelperService,
    private renderer: Renderer2,
    private legNotionalService: LegNotionalService,
    private legSpotStrikeService: LegSpotStrikeService,
    private legDateService: LegDateService,
    private adminService: AdminService,
    private legCurrencyService: LegCurrencyService,
    private pricingExtraService: PricingExtraService,
    private legMiscellaneousService: LegMiscellaneousService,
    private productsConfigService: ProductsConfigService,
    private legScheduleService: LegScheduleService
  ) {}

  public onCrossChangedNotional(notional: string): void {
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    if (form.get(notional)) {
      // if there is a notional, we need to decide where the currency we will add to the text box will come from
      // e.g base currency, quote currency or currency drop box
      if (form.get('currency')) {
        form
          .get(notional)
          .setValue(
            Utilities.notionalCurrencyRemover(form.get(notional).value)
          );
      } else {
        const notionalToAdd = counterNotionalOrPair(form, leg);
        // add the chosen currency to the textBox

        let textToAdd = ' ' + (notionalToAdd ? notionalToAdd : 'XXX');
        if (form.get('schedule')) {
          // we want to add per fixing to the end of the textbox if there is a schedule
          textToAdd = textToAdd + ' per fixing';
        }

        if (leg.productType === 'straddle') {
          textToAdd = textToAdd + ' per leg';
        }
        Utilities.endOfTextBoxAdder(
          this.legHelperService.qs('#' + notional),
          form.get(notional),
          textToAdd,
          !!notionalToAdd,
          'currency',
          this.renderer
        );
      }
    }
  }

  public onCrossChangedcounterNotional(counterNotional: string): void {
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    if (form.get(counterNotional)) {
      if (leg.quoteCurrency) {
        Utilities.endOfTextBoxAdder(
          this.legHelperService.qs('#' + counterNotional),
          form.get(counterNotional),
          ' ' + leg.quoteCurrency,
          !!leg.quoteCurrency,
          'currency',
          this.renderer
        );
      } else {
        Utilities.endOfTextBoxAdder(
          this.legHelperService.qs('#' + counterNotional),
          form.get(counterNotional),
          ' xxx',
          false,
          'currency',
          this.renderer
        );
      }
    }
  }

  public onCrossOnChangeNoValueOptionType(optionType: string): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    if (form.get(optionType) && form.get(optionType).value) {
      wipeAttributeInfo(optionType, leg); // when ever a change occurs remove the side info
    }
  }

  public setLoadedFaceCurrency(faceCurrencyNumber: '' | '1' | '2' = ''): void {
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    const cross = leg.preSetPosition['cross'];
    if (
      cross &&
      form.get('notional' + faceCurrencyNumber) &&
      form.get('counterNotional' + faceCurrencyNumber)
    ) {
      const fieldEdited =
        leg.preSetPosition['faceCurrency' + faceCurrencyNumber] ===
        cross.substring(0, 3)
          ? 'notional'
          : 'counterNotional';
      this.legNotionalService.handleNotionalSelectedRecords(
        faceCurrencyNumber,
        fieldEdited + faceCurrencyNumber
      );

      form
        .get('faceCurrency' + faceCurrencyNumber)
        .setValue(leg.preSetPosition['faceCurrency' + faceCurrencyNumber]);
    }
  }

  public onCrossChange(
    val: string,
    legSyncingInfo: LegSyncingFields,
    legIndex: number,
    fieldsToBeEdited: FieldsToBeEdited
  ): void {
    // if cross changes change some data
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    if (legSyncingInfo?.spot) {
      this.legSpotStrikeService.syncSpotsListener(legIndex);
    }

    form.get('spot')?.setValue('');

    // Clear strikes if numeric (not other shortcuts like 's', etc)
    ['strike', 'strike1', 'strike2'].forEach(field => {
      const ctrl = form.get(field);
      if (!ctrl) {
        return;
      }
      const strike = getStrikeValue(ctrl);
      if (!isNaN(+strike) && this.legHelperService.presetsLoaded) {
        ctrl.setValue('');
      }
    });
    /* if strike is taken from the delta, it requires a date.
  So ensure this is called before wiping the date */
    this.legSpotStrikeService.onStrikeCalc('');
    this.legSpotStrikeService.onStrikeCalc('1');
    this.legSpotStrikeService.onStrikeCalc('2');
    if (val && this.legHelperService.presetsLoaded) {
      this.legMiscellaneousService.fixingSourcesCutOffsFetcher(form, leg);
    }
    clearLastFixingData(form, leg);
    setupCurrencyWhenCrossChanges(
      form,
      leg,
      this.legHelperService.presetsLoaded,
      this.adminService.useDefaults,
      this.pricingExtraService.currencies
    );
    this.legDateService.setPremiumDateFromSpotDate();
    if (this.legHelperService.presetsLoaded) {
      ['', '1', '2'].forEach((fieldNumber: '' | '1' | '2') =>
        faceCurrencyChange(
          form,
          this.legHelperService.presetsLoaded,
          fieldsToBeEdited,
          leg,
          fieldNumber
        )
      );
    }
    if (val) {
      this.crossOnChangeValue(val);
    } else {
      this.crossOnChangeNoValue();
    }

    //Non-RFQable currencies to be kept as orange
    if (nonRfqAbleCrosses(val)) {
      makeTextCustomColor('cross', Utilities.orangeTextColor, leg);
    } else {
      makeTextCustomColor('cross', Utilities.whiteTextColor, leg);
    }
    this.targetProfitOnCrossChange(val);
    this.updateRelatedCrossEnums(val);
    this.legScheduleService.xccySwapGetSchedules({
      cross: val
    });
  }

  private updateRelatedCrossEnums(val: string): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const currencies = val ? [val.substring(0, 3), val.substring(3, 6)] : [];

    (
      this.productsConfigService.getAllAttributes(
        this.legHelperService.leg.attributesAsFormControls
      ) as AttributeBase<any>[]
    )
      .filter(attribute => attribute.source === 'currenciesFromCurrencyPair')
      .forEach(attribute => {
        optionsChangeHandler(
          form,
          leg,
          attribute.key,
          currencies,
          this.legHelperService.presetsLoaded ? currencies[0] || '' : null
        );
      });
  }

  private targetProfitOnCrossChange(val: string): void {
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    const targetProfitType = form.get('targetProfitType');
    const targetProfitUnit = form.get('targetProfitUnit');
    const targetProfitAdjustmentType = form.get('targetProfitAdjustmentType');
    if (
      targetProfitUnit &&
      targetProfitType &&
      targetProfitType.value !== 'Digital'
    ) {
      let tpUnitArr = [...getAllAttributeOptions('currency', leg)];
      tpUnitArr.push('Spot');
      if (targetProfitUnit.value === 'Spot') {
        targetProfitUnit.setValue('Spot');
      }

      optionsChangeHandler(form, leg, 'targetProfitUnit', tpUnitArr, 'Spot'); // XXX, XXX, Spot
    }

    if (targetProfitAdjustmentType) {
      let tpAdjArr = [...getAllAttributeOptions('currency', leg)];

      optionsChangeHandler(
        form,
        leg,
        'targetProfitAdjustmentType',
        [
          'Adjust Strike',
          'Adjust Notional',
          'Cash (' + tpAdjArr[0] + ')',
          'Cash (' + tpAdjArr[1] + ')'
        ],
        ''
      );
    }

    if (targetProfitUnit && targetProfitType) {
      if (
        leg.productType === 'choosertpf' ||
        leg.productType === 'frozenchoosertpf' ||
        leg.productType === 'chooserpivottpf'
      ) {
        optionsChangeHandler(
          // Changes to 'Common Currency' once Cross has been selected
          form,
          leg,
          'targetProfitUnit',
          ['Common Currency'],
          'Common Currency'
        );
      }

      if (leg.productType === 'simpletarf') {
        // For 'Simple TARF', TP Unit should always be 'Spot'
        if (val) {
          targetProfitUnit.setValue('Spot');
          optionsChangeHandler(form, leg, 'targetProfitUnit', ['Spot'], 'Spot');
        }
      }
    }
  }

  public onCrossChangeOptionTypeDirection(): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    leg.baseCurrency = form.get('cross').value.substring(0, 3);
    leg.quoteCurrency = form
      .get('cross')
      .value.substr(form.get('cross').value.length - 3);
    ['optionType', 'optionType1', 'optionType2'].forEach(key =>
      this.onCrossChangedOptionType(key)
    );

    if (
      form.get('cross').value &&
      form.get('direction') &&
      form.get('direction').value
    ) {
      // if a cross has been set, and there is a direction which has a value.
      // for the drop down, add the currency pair as side info
      dropDownSideInfoSetter(
        'direction',
        leg.baseCurrency + '/' + leg.quoteCurrency,
        leg
      );
    }

    setCurrencyOptions([leg.baseCurrency, leg.quoteCurrency], form, leg);
    if (rfqExecutionSettings.directRfq && form.get('currency')) {
      form.get('currency').setValue(leg.baseCurrency);
    }

    this.onCrossChangedNotional('notional');
    this.onCrossChangedNotional('notional1');
    this.onCrossChangedNotional('notional2');

    ['notionalShort', 'notionalLong'].forEach(notionalField => {
      notionalShortLongOnCrossChange(
        notionalField,
        form,
        leg,
        this.legHelperService.qs(`#${notionalField}`),
        this.renderer
      );
    });

    this.onCrossChangedcounterNotional('counterNotional');
    this.onCrossChangedcounterNotional('counterNotional1');
    this.onCrossChangedcounterNotional('counterNotional2');

    // for chooser you shouldn't be able to change the target profit unit.
    if (form.get('targetProfitUnit')) {
      optionsChangeHandler(
        form,
        leg,
        'targetProfitUnit',
        [leg.baseCurrency, leg.quoteCurrency, 'Spot'],
        ''
      );
    }

    this.legDateService.setPremiumDefaultDate();

    [
      'expiryDate',
      'settlementDate',
      'firstFixingDate',
      'firstExpirationDate',
      'firstSettlementDate',
      'windowStartDate',
      'windowEndDate',
      'windowDate'
    ].forEach(key => enableAttribute(key, form));
  }

  public onCrossChangedOptionType(optionType: string): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    if (
      leg.baseCurrency &&
      form.get(optionType) &&
      form.get(optionType).value
    ) {
      dropDownSideInfoSetter(optionType, 'on ' + leg.baseCurrency, leg); // when ever a change occurs update the side info
    }
  }

  public onSecondCrossChange(val: string): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    form.get('secondSpot')?.setValue('');
    setupCurrencyWhenCrossChanges(
      form,
      leg,
      this.legHelperService.presetsLoaded,
      this.adminService.useDefaults,
      this.pricingExtraService.currencies
    );
    if (val) {
      this.legCurrencyService.reFormatDecimalsOnCurrencyChange();
      const baseCurrency = form.get('secondCross').value.substring(0, 3);
      const quoteCurrency = form.get('secondCross').value.substring(3, 6);

      if (form.get('secondCurrency')) {
        optionsChangeHandler(
          form,
          leg,
          'secondCurrency',
          [baseCurrency, quoteCurrency],
          ''
        );
      }

      if (form.get('secondNotional')) {
        form
          .get('secondNotional')
          .setValue(
            Utilities.notionalCurrencyRemover(form.get('secondNotional').value),
            { EventEmitter: false }
          );
      }
      let shouldSpotEmitEvent = true;

      if (
        !this.legSpotStrikeService.hasSecondSpotAlreadyLoaded &&
        !leg.newProduct
      ) {
        // we dont want to emit changes on initial spot auto call
        shouldSpotEmitEvent = false;
      }
      this.legSpotStrikeService.spotCaller(
        val,
        'secondSpot',
        shouldSpotEmitEvent
      );
      this.legSpotStrikeService.hasSecondSpotAlreadyLoaded = true;
    } else {
      if (form.get('secondCurrency')) {
        optionsChangeHandler(form, leg, 'secondCurrency', [], '');
      }
    }
  }

  private crossOnChangeValue(val: string): void {
    const leg = this.legHelperService.leg;
    const legSyncingInfo = this.legHelperService.legSyncingInfo;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    this.legCurrencyService.reFormatDecimalsOnCurrencyChange();
    this.onCrossChangeOptionTypeDirection();

    let shouldSpotEmitEvent = true;

    if (!this.legSpotStrikeService.hasSpotAlreadyLoaded && !leg.newProduct) {
      // we dont want to emit changes on initial spot auto call
      shouldSpotEmitEvent = false;
    }
    if (!legSyncingInfo?.spot || !rfqExecutionSettings?.static) {
      this.legSpotStrikeService.spotCaller(val, 'spot', shouldSpotEmitEvent);
      this.legSpotStrikeService.spotCaller(
        val,
        'spotAsk',
        shouldSpotEmitEvent,
        true
      );
    }
    this.legDateService.defaultNextBusinessDay('settlementDate');
    this.legSpotStrikeService.hasSpotAlreadyLoaded = true;
  }

  private crossOnChangeNoValue(): void {
    // if the currency pair changes to no value
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    leg.baseCurrency = '';
    leg.quoteCurrency = '';
    this.onCrossOnChangeNoValueOptionType('optionType');
    this.onCrossOnChangeNoValueOptionType('optionType1');
    this.onCrossOnChangeNoValueOptionType('optionType2');

    if (form.get('direction') && form.get('direction').value) {
      wipeAttributeInfo('direction', leg); // when ever a change occurs remove the side info
    }

    setCurrencyOptions([], form, leg);

    this.onCrossChangedNotional('notional');
    this.onCrossChangedNotional('notional1');
    this.onCrossChangedNotional('notional2');

    this.onCrossChangedcounterNotional('counterNotional');
    this.onCrossChangedcounterNotional('counterNotional1');
    this.onCrossChangedcounterNotional('counterNotional2');

    [
      'expiryDate',
      'settlementDate',
      'firstFixingDate',
      'firstSettlementDate',
      'firstExpirationDate',
      'windowStartDate',
      'windowEndDate',
      'windowDate'
    ].forEach(key => disableAndWipeAttribute(key, form));
  }
}
